import React, { useRef, useState, useEffect } from "react";
import VideoPlayer from "../components/VideoPlayer";
import Notification from "../components/Notification";
import { useNotification } from "../components/useNotification";
import { brandBlue, textDark, border, white, background, container, sectionPadding, heroPadding, card } from '../styles/theme';

const SsaiVod = ({ config }) => {
  const playerRef = useRef(null);
  const [selectedStream, setSelectedStream] = useState("Sample VOD 1");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [language, setLanguage] = useState("");
  const [currentUrl, setCurrentUrl] = useState("");
  const { notification, showNotification } = useNotification();

  useEffect(() => {
    if (config && config.vodStreams && !currentUrl) {
      setCurrentUrl(config.vodStreams["Sample VOD 1"]);
    }
  }, [config, currentUrl]);

  const getStreamUrl = () => {
    try {
      // Create URL object from base stream URL
      const url = new URL(config.vodStreams[selectedStream]);
      
      // Get existing search params
      const params = url.searchParams;
      
      // Add new parameters if they exist
      if (age) params.set("ads.a", age);
      if (gender) params.set("ads.g", gender);
      if (language) params.set("ads.l", language);
      
      // Return the formatted URL
      return url.toString();
    } catch (error) {
      console.error("Error parsing URL:", error);
      showNotification('Invalid stream URL format', 'error');
      return config.vodStreams[selectedStream];
    }
  };

  const handleLoad = async () => {
    try {
      const newUrl = getStreamUrl();
      setCurrentUrl(newUrl);
    } catch (error) {
      console.error("Error loading/playing video:", error);
      showNotification('Error loading video. Please try again.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ color: textDark }}>
      {/* Hero Section with Image on Right */}
      <section className={`mx-auto ${container} px-6 ${heroPadding}`}>
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight" style={{ color: brandBlue }}>
              Dynamic ad insertion for VOD
              <span className="block text-lg md:text-2xl mt-3 font-normal" style={{ color: textDark, opacity: 0.85 }}>
                Dynamic ad insertion for video on demand
              </span>
            </h1>
            <p className="mt-4 max-w-2xl" style={{ opacity: 0.9 }}>
              Transform your VOD library into a revenue-generating asset with AWS MediaTailor-powered Server Side Ad Insertion. Our solution dynamically inserts personalized pre-roll and mid-roll ads based on viewer demographics, maximizing monetization while maintaining seamless playback experience.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              {['Demographic Targeting', 'Pre-roll Integration', 'Dynamic Personalization', 'Revenue Optimization'].map((t,i)=> (
                <span key={i} className="rounded-full px-3 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
              <div className="aspect-video">
                <img src="/images/ssai_poster.png" alt="Video Upscaling Poster" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stream Controls and Player - 2 Columns */}
      <section id="demo-section" className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          VOD Stream Demo
        </h2>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* Column 1: Stream Controls */}
          <div className="rounded-2xl p-6" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Stream Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">VOD Stream:</label>
                <select 
                  onChange={(e) => setSelectedStream(e.target.value)} 
                  value={selectedStream}
                  className="w-full p-3 border rounded-lg"
                  style={{ borderColor: border }}
                >
                  {Object.keys(config.vodStreams).map((stream) => (
                    <option key={stream} value={stream}>
                      {stream}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Age:</label>
                <input 
                  type="text" 
                  value={age} 
                  onChange={(e) => setAge(e.target.value)} 
                  placeholder="Enter age"
                  className="w-full p-3 border rounded-lg"
                  style={{ borderColor: border }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Gender:</label>
                <select 
                  onChange={(e) => setGender(e.target.value)} 
                  value={gender}
                  className="w-full p-3 border rounded-lg"
                  style={{ borderColor: border }}
                >
                  <option value="">Select Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Language:</label>
                <select 
                  onChange={(e) => setLanguage(e.target.value)} 
                  value={language}
                  className="w-full p-3 border rounded-lg"
                  style={{ borderColor: border }}
                >
                  <option value="">Select Language</option>
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="de">German</option>
                  <option value="pl">Polish</option>
                </select>
              </div>
              
              <button 
                className="w-full px-6 py-3 rounded-lg font-medium" 
                style={{ background: brandBlue, color: white }}
                onClick={handleLoad}
              >
                Load Stream
              </button>
            </div>
          </div>
          
          {/* Column 2: Video Player */}
          <div className="rounded-2xl overflow-hidden shadow-lg h-fit" style={{ border: `1px solid ${border}` }}>
            <div className="p-4" style={{ background: background, borderBottom: `1px solid ${border}` }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>Current Stream: {selectedStream}</h3>
              <p className="text-sm mt-1" style={{ opacity: 0.8 }}>
                {age && `Age: ${age} | `}{gender && `Gender: ${gender} | `}{language && `Language: ${language}`}
              </p>
            </div>
            <VideoPlayer
              label="VOD Stream"
              src={currentUrl}
              playerRef={playerRef}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          SSAI: How it works
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Ad insertion process for VOD</h3>
            <ul className="space-y-3 text-sm" style={{ color: textDark, opacity: 0.9 }}>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Content Request:</strong> Viewer requests VOD content with demographic parameters</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Ad Targeting:</strong> Age, gender, and language parameters sent to Ad Decision Server</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Dynamic Manifest:</strong> AWS MediaTailor creates personalized manifest with targeted ads</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Seamless Playback:</strong> Single stream with ads and content delivered via CloudFront</span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold mb-3" style={{ color: brandBlue }}>Benefits</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Pre-roll and mid-roll ad insertion</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Advanced demographic targeting</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Consistent ad experience across devices</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Maximize revenue from existing content</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          Technical Features & Capabilities
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "VOD Content Management",
              badges: ["S3 Storage", "HLS/DASH Ready", "DRM Support"],
              desc: "Efficiently manage and serve VOD content with AWS-native infrastructure"
            },
            {
              title: "Advanced Targeting",
              badges: ["Demographic Data", "Geographic Location", "Behavioral Insights"],
              desc: "Sophisticated ad targeting based on viewer characteristics and preferences"
            },
            {
              title: "Revenue Optimization",
              badges: ["Ad Fill Rates", "CPM Optimization", "Inventory Management"],
              desc: "Maximize ad revenue through intelligent placement and targeting strategies"
            }
          ].map((feature, i) => (
            <article key={i} className="group rounded-2xl overflow-hidden transition" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
              <div className="p-5" style={{ color: textDark }}>
                <h3 className="font-medium mb-3" style={{ color: brandBlue }}>{feature.title}</h3>
                <p className="text-sm mb-3" style={{ opacity: 0.85 }}>{feature.desc}</p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {feature.badges.map((badge, j) => (
                    <span key={j} className="rounded-full px-2.5 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>{badge}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: brandBlue }}>
        <div className={`mx-auto ${container} text-center`}>
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Monetize Your VOD Library?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Boost your on-demand revenue with our SSAI solution
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://www.bigbluemarble.com/en/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: white, color: brandBlue, textDecoration: 'none' }}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Notification notification={notification} />
    </div>
  );
};

export default SsaiVod;